import { useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import api from "../services/axios/index";
import { getAllStatuses } from "../services/admin/statuses";
import { handleApiError } from "../utils/handleApiError";
import useRoles from "./useRoles";

const FIVE_MINUTES = 5 * 60 * 1000;
const TEN_MINUTES = 10 * 60 * 1000;

/**
 * Shared hook for fetching statuses across My Tasks tabs.
 * Cached at the QueryClient level so tab unmount/remount won't re-fetch.
 */
export const useStatuses = () => {
  return useQuery({
    queryKey: ["my-tasks", "statuses"],
    queryFn: async () => {
      const data = await getAllStatuses();
      return data || [];
    },
    staleTime: FIVE_MINUTES,
    cacheTime: TEN_MINUTES,
    onError: (error) => handleApiError(error),
  });
};

/**
 * Shared hook for fetching patients across My Tasks tabs.
 * - usePatients() → all patients (used by Priority List & Dailies)
 * - usePatients({ outOfRange: true }) → out-of-range patients (separate cache entry)
 *
 * Returns the normalized patient array (handles Case Manager vs non-CM response shape).
 */
export const usePatients = ({ outOfRange = false, myTask = true, enabled = true } = {}) => {
  const { roles } = useRoles();
  const user = JSON.parse(localStorage.getItem("user"));
  const account_id = sessionStorage.getItem("acc_id");
  const location_id = JSON.parse(sessionStorage.getItem("current_loc") || "{}")?.id;

  return useQuery({
    queryKey: ["my-tasks", "patients", { outOfRange, myTask, location_id, account_id }],
    queryFn: async () => {
      if (!location_id) {
        toast.error("No Active Location Found.");
        return [];
      }

      let url;
      if (!roles.includes("Case Manager")) {
        url = `/patients/${account_id}/${location_id}/patients/`;
      } else {
        url = `/patients/${account_id}/${user?.staff_id}/staff-patients/`;
      }

      // ✅ Combine params. pagination=false => full list (this hook renders all
      // patients client-side; only the main dashboard walks paginated pages).
      const params = {
        pagination: "false",
        ...(outOfRange && { out_of_range: true }),
        ...(myTask && { my_task: true }),
      };

      const response = await api.get(url, { params });

      if (roles.includes("Case Manager")) {
        return response.data?.assigned_patients || [];
      }
      return response.data?.patients || [];
    },
    enabled: enabled && !!location_id,
    staleTime: FIVE_MINUTES,
    cacheTime: TEN_MINUTES,
    onError: (error) => handleApiError(error),
  });
};

export const usePriorityMetrics = () => {
  const account_id = sessionStorage.getItem("acc_id");
  const location_id = JSON.parse(sessionStorage.getItem("current_loc") || "{}")?.id;

  return useQuery({
    queryKey: ["my-tasks", "priority-metrics", { location_id, account_id }],
    queryFn: async () => {
      if (!location_id) {
        toast.error("No Active Location Found.");
        return {};
      }

      const response = await api.get(
        `/patients/${account_id}/${location_id}/my-tasks/priority-list/metrics/`
      );

      return response.data || {};
    },
    enabled: !!location_id,
    staleTime: FIVE_MINUTES,
    cacheTime: TEN_MINUTES,
    onError: (error) => handleApiError(error),
  });
};
